import type { Session } from '@supabase/supabase-js';

export interface AuthorizedDevice {
  id: string;
  label: string;
  createdAt: string;
  lastSeenAt: string;
}

export interface DeviceReplacementState {
  devices: AuthorizedDevice[];
  replacementLimit: number;
  replacementsUsed: number;
  nextAllowedAt?: string;
}

export interface DeviceOverview extends DeviceReplacementState {
  currentDeviceId: string;
  deviceLimit: number;
  nextReplacementAt?: string;
  usage: { studies: number; results: number };
}

interface StoredDevice {
  userId: string;
  id: string;
  label: string;
  privateKey: CryptoKey;
  publicKey: JsonWebKey;
}

interface ApiErrorBody {
  message?: string;
  code?: string;
  devices?: AuthorizedDevice[];
  replacementLimit?: number;
  replacementsUsed?: number;
  nextAllowedAt?: string;
}

export class DeviceReplacementRequiredError extends Error {
  constructor(public readonly state: DeviceReplacementState) { super('授权设备数量已满，请选择一台旧设备进行替换。'); }
}

export class DeviceReplacementLimitError extends Error {
  constructor(public readonly state: DeviceReplacementState) {
    super(state.nextAllowedAt ? `本周设备替换次数已用完，${new Date(state.nextAllowedAt).toLocaleString('zh-CN', { hour12: false })} 后可再次替换。` : '本周设备替换次数已用完。');
  }
}

let deviceSessionToken: string | undefined;
let deviceSessionUserId: string | undefined;
let deviceSessionExpiresAt = 0;
let currentAccessToken: string | undefined;
let currentAccessTokenUserId: string | undefined;

interface StoredDeviceSession {
  userId: string;
  token: string;
  expiresAt: string;
}

export async function authorizeCurrentDevice(session: Session, replaceDeviceId?: string): Promise<void> {
  updateDeviceAccessToken(session);
  if (!replaceDeviceId && restoreDeviceSession(session.user.id)) return;
  let device = await getOrCreateDevice(session.user.id);
  let registration = await register(session, device, replaceDeviceId);
  if (registration.code === 'DEVICE_REVOKED') {
    await deleteStoredDevice(session.user.id);
    device = await getOrCreateDevice(session.user.id);
    registration = await register(session, device, replaceDeviceId);
  }
  if (registration.code === 'DEVICE_REPLACEMENT_REQUIRED') throw new DeviceReplacementRequiredError(replacementState(registration));
  if (registration.code === 'DEVICE_REPLACEMENT_LIMIT') throw new DeviceReplacementLimitError(replacementState(registration));
  if (!registration.ok) throw new Error(registration.message ?? '无法授权当前设备。');
  const challenge = await accountFetch<{ challengeId: string; challenge: string }>(session, '/api/devices/challenge', { method: 'POST', body: JSON.stringify({ deviceId: device.id }) });
  const signature = await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, device.privateKey, new TextEncoder().encode(challenge.challenge));
  const verified = await accountFetch<{ deviceToken: string; expiresAt: string }>(session, '/api/devices/verify', { method: 'POST', body: JSON.stringify({ challengeId: challenge.challengeId, signature: base64Url(signature) }) });
  rememberDeviceSession(session.user.id, verified.deviceToken, verified.expiresAt);
}

export function clearDeviceSession(): void {
  if (deviceSessionUserId) removeStoredDeviceSession(deviceSessionUserId);
  deviceSessionToken = undefined;
  deviceSessionUserId = undefined;
  deviceSessionExpiresAt = 0;
  currentAccessToken = undefined;
  currentAccessTokenUserId = undefined;
}

export function hasReusableDeviceSession(userId: string): boolean {
  return restoreDeviceSession(userId);
}

export function updateDeviceAccessToken(session: Session | null | undefined): void {
  currentAccessToken = session?.access_token;
  currentAccessTokenUserId = session?.user.id;
}

export async function deviceFetch<T>(session: Session, path: string, init: RequestInit = {}, retry = true): Promise<T> {
  if (!deviceSessionToken || deviceSessionUserId !== session.user.id) await authorizeCurrentDevice(session);
  const response = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken(session)}`,
      'x-device-session': deviceSessionToken!,
      ...(init.headers ?? {}),
    },
  });
  if (response.ok) return await parseResponse<T>(response);
  const detail = await response.json().catch(() => ({})) as ApiErrorBody;
  if (retry && (detail.code === 'DEVICE_SESSION_EXPIRED' || detail.code === 'DEVICE_REQUIRED' || detail.code === 'DEVICE_REVOKED')) {
    clearDeviceSession();
    updateDeviceAccessToken(session);
    await authorizeCurrentDevice(session);
    return deviceFetch<T>(session, path, init, false);
  }
  if (detail.code === 'DEVICE_REVOKED') clearDeviceSession();
  throw new Error(detail.message ?? `请求失败（${response.status}）`);
}

export async function loadDeviceOverview(session: Session): Promise<DeviceOverview> {
  return deviceFetch<DeviceOverview>(session, '/api/devices');
}

async function register(session: Session, device: StoredDevice, replaceDeviceId?: string): Promise<ApiErrorBody & { ok: boolean }> {
  const response = await fetch('/api/devices/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken(session)}` },
    body: JSON.stringify({ deviceId: device.id, publicKey: device.publicKey, label: device.label, ...(replaceDeviceId ? { replaceDeviceId } : {}) }),
  });
  const detail = await response.json().catch(() => ({})) as ApiErrorBody;
  return { ...detail, ok: response.ok };
}

async function accountFetch<T>(session: Session, path: string, init: RequestInit): Promise<T> {
  const response = await fetch(path, { ...init, headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken(session)}`, ...(init.headers ?? {}) } });
  if (response.ok) return parseResponse<T>(response);
  const detail = await response.json().catch(() => ({})) as ApiErrorBody;
  throw new Error(detail.message ?? `请求失败（${response.status}）`);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

function replacementState(value: ApiErrorBody): DeviceReplacementState {
  return {
    devices: value.devices ?? [],
    replacementLimit: value.replacementLimit ?? 0,
    replacementsUsed: value.replacementsUsed ?? 0,
    ...(value.nextAllowedAt ? { nextAllowedAt: value.nextAllowedAt } : {}),
  };
}

async function getOrCreateDevice(userId: string): Promise<StoredDevice> {
  const stored = await readStoredDevice(userId);
  if (stored) return stored;
  if (!globalThis.crypto?.subtle || typeof indexedDB === 'undefined') throw new Error('当前浏览器不支持安全设备凭据，请升级浏览器后重试。');
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']) as CryptoKeyPair;
  const device: StoredDevice = {
    userId,
    id: crypto.randomUUID(),
    label: deviceLabel(),
    privateKey: pair.privateKey,
    publicKey: await crypto.subtle.exportKey('jwk', pair.publicKey),
  };
  await writeStoredDevice(device);
  return device;
}

function deviceLabel(): string {
  const source = `${navigator.userAgent} ${navigator.platform}`.toLowerCase();
  if (source.includes('iphone')) return 'iPhone';
  if (source.includes('ipad')) return 'iPad';
  if (source.includes('android')) return 'Android 设备';
  if (source.includes('mac')) return 'Mac';
  if (source.includes('windows')) return 'Windows 电脑';
  if (source.includes('linux')) return 'Linux 设备';
  return '浏览器设备';
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('minglun-security', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('devices', { keyPath: 'userId' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('无法打开设备凭据存储。'));
  });
}

async function readStoredDevice(userId: string): Promise<StoredDevice | undefined> {
  const database = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const request = database.transaction('devices').objectStore('devices').get(userId);
      request.onsuccess = () => resolve(request.result as StoredDevice | undefined);
      request.onerror = () => reject(request.error);
    });
  } finally { database.close(); }
}

async function writeStoredDevice(device: StoredDevice): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('devices', 'readwrite');
      transaction.objectStore('devices').put(device);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally { database.close(); }
}

async function deleteStoredDevice(userId: string): Promise<void> {
  const database = await openDatabase();
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('devices', 'readwrite');
      transaction.objectStore('devices').delete(userId);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  } finally { database.close(); }
}

function base64Url(value: ArrayBuffer): string {
  let binary = '';
  new Uint8Array(value).forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function accessToken(session: Session): string {
  return currentAccessTokenUserId === session.user.id && currentAccessToken
    ? currentAccessToken
    : session.access_token;
}

function rememberDeviceSession(userId: string, token: string, expiresAt: string): void {
  deviceSessionToken = token;
  deviceSessionUserId = userId;
  deviceSessionExpiresAt = new Date(expiresAt).getTime();
  try {
    sessionStorage.setItem(deviceSessionStorageKey(userId), JSON.stringify({ userId, token, expiresAt } satisfies StoredDeviceSession));
  } catch { /* A private browser may deny session storage; memory reuse still works. */ }
}

function restoreDeviceSession(userId: string): boolean {
  if (deviceSessionUserId === userId && deviceSessionToken && isSessionFresh(deviceSessionExpiresAt)) return true;
  try {
    const raw = sessionStorage.getItem(deviceSessionStorageKey(userId));
    if (!raw) return false;
    const stored = JSON.parse(raw) as Partial<StoredDeviceSession>;
    const expiresAt = typeof stored.expiresAt === 'string' ? new Date(stored.expiresAt).getTime() : 0;
    if (stored.userId !== userId || typeof stored.token !== 'string' || !isSessionFresh(expiresAt)) {
      sessionStorage.removeItem(deviceSessionStorageKey(userId));
      return false;
    }
    deviceSessionToken = stored.token;
    deviceSessionUserId = userId;
    deviceSessionExpiresAt = expiresAt;
    return true;
  } catch {
    return false;
  }
}

function isSessionFresh(expiresAt: number): boolean {
  return Number.isFinite(expiresAt) && expiresAt > Date.now() + 30_000;
}

function removeStoredDeviceSession(userId: string): void {
  try { sessionStorage.removeItem(deviceSessionStorageKey(userId)); } catch { /* Ignore unavailable storage. */ }
}

function deviceSessionStorageKey(userId: string): string {
  return `minglun.device-session.${userId}`;
}
